import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { LifeBuoy, Mail } from "lucide-react";
import {
  SUPPORT_ROUTES,
  supportListResponseSchema,
  supportMessageResponseSchema,
  type SupportMessage,
  type SupportQuery,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { GlassPanel, PageHeader } from "@/shared/ui/layout";
import { Badge, Button, Select } from "@/shared/ui/core";
import { EmptyState, SkeletonTable } from "@/shared/ui/ui";
import { AdminGuard, DataTableFrame, Pagination,
  DEFAULT_PER_PAGE,
} from "@/features/admin/components";
import { formatOrderDate } from "@/features/admin/orders/format";

const NO_PREFIX = { baseUrl: "" } as const;

function listMessages(query: SupportQuery) {
  const params = new URLSearchParams();
  params.set("page", String(query.page));
  params.set("perPage", String(query.perPage));
  if (query.status) params.set("status", query.status);
  return authFetch(
    `${SUPPORT_ROUTES.adminList}?${params.toString()}`,
    supportListResponseSchema,
    NO_PREFIX,
  );
}

function setResolved(id: string, resolved: boolean): Promise<SupportMessage> {
  return authFetch(SUPPORT_ROUTES.adminOne(id), supportMessageResponseSchema, {
    ...NO_PREFIX,
    method: "PATCH",
    body: { resolved },
  }).then((data) => data.message);
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * Soporte — réplica de `admin/support` (Anahí, 27-ago): el buzón del
 * formulario de contacto (los mensajes SIEMPRE se guardaron; esta es
 * la pantalla que faltaba para leerlos). Responder es "Responder por
 * correo" — abre el correo con el remitente puesto; aquí se marca
 * atendido y ya.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminSupportPage() {
  return (
    <AdminGuard>
      <SupportScreen />
    </AdminGuard>
  );
}

function SupportScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  /*
   * Las filas por página las elige quien mira (Anahí, 29-ago): 5, 10 o 20,
   * con CINCO por defecto. Vive en el estado y no como constante porque
   * ahora cambia en caliente; al cambiarlo se vuelve a la página 1, que es
   * lo único sensato — la página 4 de 5 en 5 no es la página 4 de 20 en 20.
   */
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);

  const query: SupportQuery = {
    page,
    perPage: perPage,
    ...(status ? { status: status as "open" | "resolved" } : {}),
  };
  const messages = useQuery({
    queryKey: ["admin", "support", query],
    queryFn: () => listMessages(query),
    placeholderData: (previous) => previous,
  });

  const marcar = useMutation({
    mutationFn: ({ id, resolved }: { id: string; resolved: boolean }) => setResolved(id, resolved),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin", "support"] }),
  });

  const items = messages.data?.items ?? [];
  const total = messages.data?.total ?? 0;

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Soporte")}
        description={t("Lo que llega por el formulario de contacto, con su estado de atención.")}
      />

      {/* ── Un solo filtro, limpio ─────────────────────────────────────── */}
      <div className="mb-4 w-44">
        <Select
          aria-label={t("Estado")}
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
          placeholder={t("Todos")}
          options={[
            { value: "open", label: t("Por atender") },
            { value: "resolved", label: t("Atendidos") },
          ]}
        />
      </div>

      <DataTableFrame aria-busy={messages.isFetching}>
        {messages.isPending ? (
          <div className="p-5">
            <SkeletonTable rows={5} columns={4} />
          </div>
        ) : items.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={<LifeBuoy className="h-5 w-5 text-ds-ink-3" />}
              title={t("Buzón vacío")}
              description={t("Los mensajes del formulario de contacto llegan aquí.")}
            />
          </div>
        ) : (
          <ul className="grid gap-3 p-4">
            {items.map((message) => (
              <li key={message.id}>
                <GlassPanel className="relative p-4">
                  <div className="relative flex flex-wrap items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-[14px] font-semibold text-ds-ink">{message.name}</p>
                        <a
                          href={`mailto:${message.email}`}
                          className="text-[12px] text-ds-violet-pop hover:underline"
                        >
                          {message.email}
                        </a>
                        <Badge
                          variant={message.resolvedAt ? "success" : "warning"}
                          size="sm"
                          dot
                        >
                          {message.resolvedAt ? t("Atendido") : t("Por atender")}
                        </Badge>
                      </div>
                      <p className="mt-2 whitespace-pre-line text-[14px] leading-relaxed text-ds-ink-3">
                        {message.message}
                      </p>
                      <p className="mt-2 text-[12px] text-ds-ink-3">
                        {formatOrderDate(message.createdAt)}
                        {message.resolvedAt &&
                          ` · ${t("atendido el {{fecha}}", { fecha: formatOrderDate(message.resolvedAt) })}`}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Button
                        as="a"
                        href={`mailto:${message.email}?subject=${encodeURIComponent("Re: tu mensaje a It's The Best")}`}
                        variant="secondary"
                        size="sm"
                        leftIcon={<Mail />}
                      >
                        {t("Responder")}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={marcar.isPending && marcar.variables?.id === message.id}
                        onClick={() =>
                          marcar.mutate({ id: message.id, resolved: !message.resolvedAt })
                        }
                      >
                        {message.resolvedAt ? t("Reabrir") : t("Marcar atendido")}
                      </Button>
                    </div>
                  </div>
                </GlassPanel>
              </li>
            ))}
          </ul>
        )}

        {total > 0 && (
          <Pagination page={page} perPage={perPage} total={total} onPageChange={setPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
        )}
      </DataTableFrame>
    </>
  );
}
